import { useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Grid,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import { RootState, store } from "@store/store";
// import { Condition } from "@interfaces/application";
import { ContractualRevision } from "@interfaces/revision";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { RevisionCheckPointDialogProps } from "@interfaces/slices";
import {
    useLazyGetApplicationQuery,
    useLazyGetConditionsQuery,
    useLazyGetMotorbikeDetailQuery,
} from "@services/api/applications";
import { useLazyGetContractualRevisionQuery } from "@services/api/revision";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency } from "@helpers/formats";

export const PuntoRevision = (props: RevisionCheckPointDialogProps) => {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    // const [openingFee, setOpeningFee] = useState<number>(0);
    // const [currentCondition, setCurrenCondition] = useState<Condition | undefined>(undefined);
    const [conditions, setConditions] = useState<Array<{ title: string; value: string | number }>>([]);
    const [result, setResult] = useState<ContractualRevision>({
        data: {
            documents: [],
            folder_name: "",
            tennant: "",
            singer_provider: "",
        },
        message: "",
        success: true,
    });

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const [triggerGetApplication] = useLazyGetApplicationQuery();
    const [triggerGetCurrentCondition] = useLazyGetConditionsQuery();
    const [triggerGetMotorbikeDetail] = useLazyGetMotorbikeDetailQuery();
    const [triggerGetRevisionContractuales, { isFetching: isFetchingGetRevisionContractuals }] =
        useLazyGetContractualRevisionQuery();

    // const applicationSelected = (state = store.getState()) => state.components.applicationSelected;

    const loadInfo = async () => {
        try {
            if (["CSEVD", "CSVDE"].includes(props.checkPointType)) {
                const result = await triggerGetRevisionContractuales(applicationSelected.id).unwrap();
                setResult(result);

                if (["CSCM1", "CSCM2"].includes(applicationSelected.tax_system.code)) {
                    const motorbikeDetail = await triggerGetMotorbikeDetail({
                        applicationId: applicationSelected.id,
                        motorbikeDetailId: applicationSelected.motorbike_detail_id,
                    }).unwrap();

                    setConditions([
                        {
                            title: "Precio activo",
                            value: formatCurrency(motorbikeDetail.approved_details.active_price, "$", 0),
                        },
                        {
                            title: "Placas y tenencia",
                            value: formatCurrency(motorbikeDetail.approved_details.license_plates_ownership, "$", 0),
                        },
                        {
                            title: "Seguro",
                            value: formatCurrency(motorbikeDetail.approved_details.insurance_18m, "$", 0),
                        },
                        {
                            title: "Costo Total",
                            value: formatCurrency(motorbikeDetail.approved_details.total_cost, "$", 2),
                        },
                        {
                            title: "Plazo Autorizado",
                            value: `${motorbikeDetail.approved_details.authorized_period} semanas`,
                        },
                        {
                            title: "Tasa anual",
                            value: `${motorbikeDetail.approved_details.annual_fee}%`,
                        },
                        {
                            title: "Enganche",
                            value: `${motorbikeDetail.approved_details.down_payment}%`,
                        },
                        {
                            title: "Vigencia",
                            value: `${motorbikeDetail.approved_details.validity_days} días`,
                        },
                        {
                            title: "Pago Semanal",
                            value: formatCurrency(motorbikeDetail.approved_details.weekly_payments, "$", 0),
                        },
                    ]);
                } else {
                    const appInfo = await triggerGetApplication(applicationSelected.id, false).unwrap();
                    // setOpeningFee(appInfo.opening_fee);

                    const searchParams = new URLSearchParams([["q", "active:true"]]);
                    const response = await triggerGetCurrentCondition({
                        applicationId: applicationSelected.id ?? "0-0-0-0-0",
                        params: searchParams.toString(),
                    }).unwrap();

                    const activeCondition = response.at(0);

                    setConditions([
                        {
                            title: "Monto seleccionado",
                            value: formatCurrency(activeCondition?.selected_amount ?? 0, "$", 0),
                        },
                        {
                            title: "Monto máximo autorizado",
                            value: formatCurrency(activeCondition?.approved_amount ?? 0, "$", 0),
                        },
                        {
                            title: "Plazo seleccionado",
                            value: formatCurrency(activeCondition?.selected_term ?? 0, "$", 0),
                        },
                        {
                            title: "Plazo autorizado",
                            value: `Min: ${activeCondition?.approved_min_term} - Max: ${activeCondition?.approved_max_term}`,
                        },
                        {
                            title: "Tasa autorizada",
                            value: formatCurrency(activeCondition?.interest_rate ?? 0, "$", 0),
                        },
                        {
                            title: "Comisión por apertura",
                            value: `${appInfo.opening_fee}%`,
                        },
                    ]);
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (hasFetched.current || applicationSelected.id === "0-0-0-0-0") return;
        loadInfo();

        hasFetched.current = true;
    }, [applicationSelected]);

    return (
        <Box margin={4} py={2} maxHeight={"60vh"} minWidth={"250px"}>
            <Stack rowGap={2}>
                <Typography variant="body1" color="main3" fontWeight={600} textAlign={"center"}>
                    Checkpoint {props.title}
                </Typography>

                <Grid container>
                    <Grid size={12}>
                        <Typography variant="h5" fontWeight={500}>
                            Condiciones de crédito
                        </Typography>
                    </Grid>

                    {conditions.map((condition) => (
                        <>
                            <Grid size={3}>
                                <Typography>{condition.title}</Typography>
                            </Grid>
                            <Grid size={3}>
                                <Typography>{condition.value}</Typography>
                            </Grid>
                        </>
                    ))}
                </Grid>

                {isFetchingGetRevisionContractuals ? (
                    <Accordion expanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Skeleton variant="text" width={"100%"} />
                        </AccordionSummary>
                        <AccordionDetails>
                            <Skeleton variant="text" />
                            <Skeleton variant="text" />
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <>
                        {result?.success === false && (
                            <Alert variant="filled" severity="warning">
                                {result?.message}
                            </Alert>
                        )}
                        {Array.isArray(result?.data) === false &&
                            result?.data.documents.map((document, idx) => {
                                return (
                                    <Accordion key={idx}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body1" color="main3" fontWeight={600}>
                                                {document.file_name}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body1" color="main3" fontWeight={300}>
                                                Firmantes:
                                            </Typography>
                                            <TableContainer variant="emphasis">
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Nombre</TableCell>
                                                            <TableCell>Correo</TableCell>
                                                            <TableCell>Teléfono</TableCell>
                                                            <TableCell>RFC</TableCell>
                                                            <TableCell>CURP</TableCell>
                                                            <TableCell>Tipo</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {document.signers.map((signer, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{signer.name}</TableCell>
                                                                <TableCell>{signer.email}</TableCell>
                                                                <TableCell>{signer.phone}</TableCell>
                                                                <TableCell>{signer.associates.rfc}</TableCell>
                                                                <TableCell>{signer.associates.curp}</TableCell>
                                                                <TableCell>
                                                                    {signer.type === "external"
                                                                        ? `Externo`
                                                                        : `Interno - ${signer.role}`}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                    </>
                )}
            </Stack>
            <Stack direction={"row"} justifyContent={"space-around"} mt={3} pb={2}>
                <Button variant="blue_outlined" onClick={() => dispatch(setDialogConfig())}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    sx={{ maxWidth: "250px" }}
                    onClick={props.onAcceptClick}
                    disabled={result?.success === false}
                >
                    {props.checkPointType === "CSEVD" ? "Continuar con la liberación para Revisión Ext" : ""}
                    {props.checkPointType === "CSVDE" ? "Continuar con la liberación para firma" : ""}
                </Button>
            </Stack>
        </Box>
    );
};
